/* eslint-disable no-prototype-builtins */
import * as React from 'react';
import { mapValues } from 'lodash';
import currency from 'currency.js';
import { connect } from 'react-redux';

import { Form } from 'components/form';
import Spinner from 'components/spinner/Spinner';
import ActionButton from 'components/buttons/action-button/ActionButton';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';
import CheckboxesList from 'components/inputs/checkboxes-list/CheckboxesList';
import { ModalFrame, ModalFrameFooter, ModalFrameInner } from 'components/modal';
import CCABulkSubtotal from './components/subtotal/CCABulkSubtotal';

import PromptExit from '../../../../libs/router/prompt-exit/PromptExit';

import CostAttributionBulkItem from '../cost-attribution-bulk-item/CostAttributionBulkItem';
import CostAttributionTileInput, { SUPPORTED_ATTRIBUTION_DELIVERABLES } from '../cost-attribution-tile/input/CostAttributionTileInput';

import {
  calculateBulkCostAttributions,
  fetchBulkCostAttribution,
  resetBulkCostAttribution
} from 'modules/reporting/reducers/bulk-cost-attribution';

import { isDefined } from 'utils/helper/common';
import { __t, applyTranslations } from 'utils/language';
import { DELIVERABLE_ADDITIONAL_COSTS, DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION } from 'utils/deliverables';
import { calculateTotalCCACosts } from 'modules/reporting/utils/cca-costs';

import type {
  BudgetSplitResourceV2,
  CalculatedBudgetSplitResource
} from 'modules/reporting/types/BudgetSplitResource.type';
import type {
  BulkCostAttributionLoadingState,
  BulkCostAttributionsSet,
  CostAttributionSlice
} from 'modules/reporting/reducers/bulk-cost-attribution';

import './cost-attribution-bulk-modal.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./cost-attribution-bulk-modal.i18n.json'));

type OwnProps = {
  campaignId: number,
  splits: Array<BudgetSplitResourceV2>,
  onCloseModal: () => void,
  onReady: (data: Array<CalculatedBudgetSplitResource>) => void
};

type StateProps = {
  slices: Array<CostAttributionSlice>,
  loadingState: BulkCostAttributionLoadingState
};

type DispatchProps = {
  dispatchResetBulkCostAttribution: () => void,
  dispatchFetchBulkCostAttribution: (campaignId: number) => void,
  dispatchCalculateBulkCostAttributions: (
    campaignId: number,
    data: Array<BulkCostAttributionsSet>
  ) => Promise<Array<CalculatedBudgetSplitResource>>
};

type CostAttributionState = Record<string, string>;

type Props = OwnProps & StateProps & DispatchProps;

const CostAttributionBulkModal: React.FC<Props> = (
  {
    campaignId,
    splits,
    slices,
    loadingState,
    onCloseModal,
    onReady,
    dispatchResetBulkCostAttribution,
    dispatchFetchBulkCostAttribution,
    dispatchCalculateBulkCostAttributions
  }: Props
) => {
  React.useEffect(() => {
    dispatchFetchBulkCostAttribution(campaignId);

    return dispatchResetBulkCostAttribution;
  }, [campaignId]);

  const [newSplits, setNewSplits] = React.useState<Array<BulkCostAttributionsSet>>([]);
  const [selectedOffers, setSelectedOffers] = React.useState<Array<number>>([]);
  const [sliceStep, setSliceStep] = React.useState(0);
  const [costAttribution, setCostAttribution] = React.useState<CostAttributionState>({});
  const [lastEditedType, setLastEditedType] = React.useState<string>('');

  React.useEffect(() => {
    const currentSlice = slices[sliceStep];
    if (!currentSlice) {
      return;
    }

    setSelectedOffers([...currentSlice.offerIds]);
  }, [slices]);

  // let's use currency anyway to avoid float value errors
  const totalEnteredPercentage = Object.keys(costAttribution)
    .reduce(calculateTotalCCACosts(costAttribution), currency(0))
    .value;
  const isExceeded100 = totalEnteredPercentage > 100;
  const isBelowThe100 = totalEnteredPercentage < 100;

  const steps = loadingState === 'loaded' && slices.length > 1
    ? `${sliceStep + 1}/${slices.length}`
    : '';

  if (loadingState === 'initial' || loadingState === 'loading') {
    return (
      <ModalFrame title="">
        <Spinner size="small" position="inline" color="light" />
      </ModalFrame>
    );
  }

  if (loadingState === 'failed') {
    onCloseModal();
    return null;
  }

  // expect data being loaded here
  const hasMoreSteps = slices.length > (sliceStep + 1);
  const currentSlice = slices[sliceStep];
  const currentCreators = splits
    .filter((entry) => currentSlice.offerIds.includes(entry.budgetSplit.offerId));

  const groupedDeliverables = splits
    .filter((entry) => currentSlice.offerIds.includes(entry.budgetSplit.offerId))
    .flatMap(split => split.budgetSplit.taskSplits)
    .filter(entry => entry.count > 0)
    .map(({ deliverableType, count }) => ({ deliverableType, count }))
    .reduce((result, item) => ({
      ...result,
      [String(item.deliverableType)]: item.count
    }), {});

  const hasUnsavedChanges = newSplits.some(split => split.offerIds.length > 0)
    || Object.values(costAttribution).filter(isDefined).length > 0;

  const hasEnteredAdditionalCosts = costAttribution[DELIVERABLE_ADDITIONAL_COSTS]
    && costAttribution[DELIVERABLE_ADDITIONAL_COSTS] !== '0';

  function processModalPage(diff: number): Array<BulkCostAttributionsSet> {
    const newCombinedSplits = [...newSplits];
    newCombinedSplits[sliceStep] = {
      offerIds: selectedOffers,
      budgetSplit: mapValues(costAttribution, (str, type) => {
        if (type === DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION) {
          return str;
        }

        return parseFloat(str) || 0;
      })
    };
    setNewSplits(newCombinedSplits);

    setCostAttribution(mapValues(newSplits[sliceStep + diff]?.budgetSplit ?? {}, (val) => String(val)));

    const nextSliceOfferIdsFallback = (diff !== 0) ? slices[sliceStep + diff]?.offerIds ?? [] : [];
    setSelectedOffers(newSplits[sliceStep + diff]?.offerIds ?? nextSliceOfferIdsFallback);

    if (diff) {
      setSliceStep(sliceStep + diff);
    }

    return newCombinedSplits;
  }

  return (
    <ModalFrame
      title={__t('cost_attribution_bulk_modal.title', { steps })}
      subtitle={__t('cost_attribution_bulk_modal.subtitle')}
      classNameFrame="cost-attribution-bulk-modal"
    >
      {
        hasUnsavedChanges &&
        <PromptExit
          type="modal"
          title={__t('cost_attribution_bulk_modal.exit_modal_title')}
          message={__t('cost_attribution_bulk_modal.exit_modal_text')}
        />
      }

      <Form
        onSubmit={async () => {
          const newCombinedSplits = processModalPage(hasMoreSteps ? 1 : 0);

          const nonEmptySplits = newCombinedSplits.filter(val => val.offerIds.length > 0);

          if (!hasMoreSteps && nonEmptySplits.length > 0) {
            const recalculatedSplits = await dispatchCalculateBulkCostAttributions(campaignId, nonEmptySplits);
            onReady(recalculatedSplits);
          }

          if (!hasMoreSteps) {
            onCloseModal();
          }
        }}
      >
        <ModalFrameInner>
          <div className="cost-attribution-bulk-modal__content">
            <div className="cost-attribution-bulk-modal__subtitle">
              {__t('cost_attribution_bulk_modal.enter_distributions_title')}
            </div>

            <VerticalSpacing type="medium" />

            <div className="cost-attribution-bulk-modal__inputs">
              {
                SUPPORTED_ATTRIBUTION_DELIVERABLES.map((type: string) => {
                  if (groupedDeliverables.hasOwnProperty(type) || type === DELIVERABLE_ADDITIONAL_COSTS) {
                    return (
                      <CostAttributionTileInput
                        key={`step-${sliceStep}-${type}`}
                        mode="percentage"
                        type={type}
                        canBeSkipped={selectedOffers.length === 0}
                        value={costAttribution[type]}
                        isExceededBudget={lastEditedType === type && isExceeded100}
                        isBelowTheBudget={isBelowThe100}
                        amount={groupedDeliverables[type] ?? 0}
                        onChange={(newValue: string) => {
                          const newAttribution = {
                            ...costAttribution,
                            [type]: newValue
                          };

                          setLastEditedType(type);
                          setCostAttribution(newAttribution);
                        }}
                      />
                    );
                  }

                  return null;
                })
              }
              <CostAttributionTileInput
                key={DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION}
                type={DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION}
                value={costAttribution[DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION]}
                mode="percentage"
                isExceededBudget={false}
                isBelowTheBudget={false}
                amount={0}
                canBeSkipped={!hasEnteredAdditionalCosts}
                mustBeEmpty={!hasEnteredAdditionalCosts}
                onChange={(newValue: string) => {
                  const newAttribution = {
                    ...costAttribution,
                    [DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION]: newValue
                  };

                  setLastEditedType(DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION);
                  setCostAttribution(newAttribution);
                }}
              />
            </div>

            <CCABulkSubtotal totalEnteredPercentage={totalEnteredPercentage} />

            <VerticalSpacing type="medium" />

            <div className="cost-attribution-bulk-modal__creators">
              <div className="cost-attribution-bulk-modal__subtitle">
                {__t('cost_attribution_bulk_modal.select_creators_title')}
              </div>

              <VerticalSpacing type="medium" />

              <CheckboxesList
                customContainerClass="cost-attribution-bulk-modal__checkbox-container-wrapper"
                customItemClass="cost-attribution-bulk-modal__item-wrapper"
                emptyLabel=""
                lists={
                  currentCreators.map(entry => ({
                    id: entry.creator.id,
                    creator: entry.creator,
                    budgetSplit: entry.budgetSplit,
                    isSelected: selectedOffers.includes(entry.budgetSplit.offerId),
                    isDisabled: false
                  }))
                }
                labelComponent={({ creator, budgetSplit }) => (
                  <CostAttributionBulkItem
                    creator={creator}
                    budgetSplit={budgetSplit}
                  />
                )}
                onCheckboxChange={(event, { budgetSplit }) => {
                  const offerId = budgetSplit.offerId;
                  if (selectedOffers.includes(offerId)) {
                    setSelectedOffers(selectedOffers.filter(id => id !== offerId));
                  } else {
                    setSelectedOffers([...selectedOffers, offerId]);
                  }
                }}
              />
            </div>
          </div>
        </ModalFrameInner>
        <ModalFrameFooter>
          <ActionButton
            color="tertiary"
            onClick={onCloseModal}
          >
            {__t('collabary_common.action_button.cancel')}
          </ActionButton>
          {
            (sliceStep > 0) && (
              <ActionButton
                color="secondary"
                onClick={() => {
                  processModalPage(-1);
                }}
              >
                {__t('collabary_common.action_button.back')}
              </ActionButton>
            )
          }
          <ActionButton
            type="submit"
            color="secondary"
          >
            {
              hasMoreSteps
                ? __t('collabary_common.action_button.next')
                : __t('collabary_common.action_button.apply')
            }
          </ActionButton>
        </ModalFrameFooter>
      </Form>
    </ModalFrame>
  );
};

function mapStateToProps({ bulkCostAttribution }): StateProps {
  return {
    slices: bulkCostAttribution.data,
    loadingState: bulkCostAttribution.currentState
  };
}

function mapDispatchToProps(dispatch): DispatchProps {
  return {
    dispatchResetBulkCostAttribution: () => dispatch(resetBulkCostAttribution()),
    dispatchFetchBulkCostAttribution: (campaignId: number) => dispatch(fetchBulkCostAttribution(campaignId)),
    dispatchCalculateBulkCostAttributions: (campaignId: number, data: Array<BulkCostAttributionsSet>) =>
      dispatch(calculateBulkCostAttributions(campaignId, data))
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CostAttributionBulkModal);
